import { useField, useFormikContext } from 'formik';

import type { Address } from '@core/types/api/Address/Address';
import type { LocationResult } from '@core/types/api/LocationResult/LocationResult';
import type { ExcludeMethodsDeep } from '@core/utils/types';

interface UseAddressProps {
  name: string;
}

interface ReturnAddressProps {
  handleAddressChange(location: Partial<LocationResult>): ExcludeMethodsDeep<Address>;
}

export const useAddress = ({ name }: UseAddressProps): ReturnAddressProps => {
  const [field] = useField<ExcludeMethodsDeep<Address>>(name);
  const { setFieldValue, setFieldTouched } = useFormikContext();

  const handleAddressChange = (location: LocationResult): ExcludeMethodsDeep<Address> => {
    // Create a completely new object to ensure Formik detects the change
    // Using object spread instead of merge to guarantee a new reference
    const newAddress: ExcludeMethodsDeep<Address> = {
      ...field.value,
      line1: location?.address?.line1,
      line2: field?.value?.line2,
      city: location?.address?.city,
      state: location?.address?.state,
      country: location?.address?.country,
      postalCode: location?.address?.postalCode,
      latitude: location?.location?.lat?.toString() ?? field?.value?.latitude ?? null,
      longitude: location?.location?.lng?.toString() ?? field?.value?.longitude ?? null,
    };

    setFieldTouched(name, true);
    setFieldValue(name, newAddress);

    return newAddress;
  };

  return {
    handleAddressChange,
  };
};
